import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { IssueIcon, thematicClasses } from "@/lib/thematic";
import { ResourceMenu } from "@/features/resources";

import type { ActionPlanAction, ActionPlanDetail } from "../../api/types";
import { actionName, goalName } from "../../lib/selection";
import { ActionEditor } from "../ActionEditor";
import { StepSideNav } from "../StepSideNav";
import { goalAnchor, jumpToGoal } from "../../lib/anchors";
import { buildNavIssues } from "../../lib/step-nav";
import { StepIntro } from "./StepIntro";

/**
 * An action counts as planned once it carries any of this step's detail. Used
 * for the nav tally, so you can see at a glance which objectives are still
 * untouched without scrolling through every form.
 */
function isPlanned(action: ActionPlanAction): boolean {
  return Boolean(
    action.responsible_name ||
      action.start_date ||
      action.deadline ||
      action.target ||
      action.estimated_cost != null ||
      action.estimated_cost_hours != null,
  );
}

/**
 * Step 4 — "Plan actions" (legacy "Planification des actions"). For each
 * selected course of action, capture start/deadline, owner, target and cost.
 * Status is not here — it lives in the Track step. Grouped by thematic → issue
 * → goal, colour-coded by the sustainability pillar, with a side nav: this step
 * renders a form per retained action, so it is the longest of the five.
 */
export function PlanStep({ plan }: { plan: ActionPlanDetail }) {
  const { t } = useTranslation();

  // Only retained actions are planned here, and an issue or objective with none
  // left has nothing to show.
  const issues = plan.action_plan_thematics.flatMap((thematic) =>
    thematic.action_plan_issues
      .map((issue) => ({
        issue,
        thematic,
        goals: issue.action_plan_goals
          .map((goal) => ({
            goal,
            actions: goal.action_plan_actions.filter((a) => a.is_selected),
          }))
          .filter((g) => g.actions.length > 0),
      }))
      .filter((entry) => entry.goals.length > 0),
  );

  // Nav: how many actions carry planning detail, flagging the untouched ones.
  const navIssues = buildNavIssues(plan.action_plan_thematics, {
    actionsOf: (goal) => goal.action_plan_actions.filter((a) => a.is_selected),
    tally: (actions) => {
      const planned = actions.filter(isPlanned).length;
      return { count: `${planned}/${actions.length}`, warn: planned === 0 };
    },
  });

  if (issues.length === 0) {
    return (
      <div className="space-y-6">
        <StepIntro
          heading={t("actionPlans.plan.heading")}
          help={t("actionPlans.plan.help")}
        />
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground">
              {t("actionPlans.plan.noSelected")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <StepIntro
        heading={t("actionPlans.plan.heading")}
        help={t("actionPlans.plan.help")}
      />

      {/* Explicit minmax(0,…) tracks: an `auto` track sizes to its content's
          min-content, and a truncating label reports its whole string there. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
        <StepSideNav issues={navIssues} onJump={jumpToGoal} />

        <div className="space-y-4">
          {issues.map(({ issue, thematic, goals }) => {
            const tc = thematicClasses(thematic.css_class);
            return (
              <div key={issue.id} className="overflow-hidden rounded-xl border">
                <div
                  className={cn(
                    "flex items-center gap-2.5 px-4 py-3 text-sm font-bold",
                    tc.softBg,
                    tc.ink,
                  )}
                >
                  <IssueIcon icon={issue.icon} className="size-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{issue.name}</span>
                </div>
                <div className="space-y-5 p-4">
                  {goals.map(({ goal, actions }) => (
                    <div
                      key={goal.id}
                      id={goalAnchor(goal.id)}
                      className="scroll-mt-4 space-y-3"
                    >
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        {goalName(goal)}
                      </h4>
                      {actions.map((action) => (
                        <div
                          key={action.id}
                          className="rounded-lg border border-border p-4"
                        >
                          {/* The resources sit on the title row: this step has
                              no comment icon to sit beside (unlike the Django
                              page) — comments are a labelled autosave field
                              inside ActionEditor. */}
                          <div className="mb-2 flex items-start gap-2">
                            <p className="flex-1 font-medium">{actionName(action)}</p>
                            <ResourceMenu
                              actionId={action.original_action}
                              actionName={actionName(action)}
                              directories={plan}
                              availability={action}
                              variant="short"
                              favoritesDefault
                            />
                          </div>
                          <ActionEditor action={action} planId={plan.id} mode="planning" />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
